import React, { useState, useEffect, ReactElement } from "react";
import { Grid, Input, Typography } from "@material-ui/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { hideModal } from "../../../usecases/commons/hide-modal";
import changeAlertThreshold from "../../../usecases/my-account/update-alert-threshold";
import { translate } from "../../translate/localization";
import { MonetaryAmount } from "../../../app/domain/monetary-amount";
import Modal from "../../../app/components/modals/Modal";
import CobeeButton from "../../../app/components/CobeeButton";
import EmailSection from "./components/EmailSection";

import useStyles from "./styles";

interface AlertThresholdForm {
  amount: string;
  emails: string[];
}

function AlertThresholdModal({
  alertThreshold,
  alertThresholdEmails,
}: {
  alertThreshold: MonetaryAmount;
  alertThresholdEmails: string[];
}): ReactElement {
  const {
    container,
    titleStyles,
    section,
    description,
    label,
    inputContainer,
    inputStyle,
    buttonSpacing,
    buttonContainer,
    linkStyle,
    linkContainer,
  } = useStyles();
  const [loading, setLoading] = useState(false);
  const [showInputEmail, setShowInputEmail] = useState(false);
  const [formData, setFormData] = useState<AlertThresholdForm>({
    amount: "",
    emails: [],
  });

  useEffect(() => {
    setFormData({
      amount: String(alertThreshold),
      emails: alertThresholdEmails,
    });
  }, [alertThreshold, alertThresholdEmails]);

  const showInput = () => {
    setShowInputEmail(true);
  };

  const handleSaveEmail = (email: string) => {
    setFormData({ ...formData, emails: [...formData.emails, email] });
    setShowInputEmail(false);
  };

  const handleEditEmail = ({
    oldEmail,
    email,
  }: {
    oldEmail: string;
    email: string;
  }) => {
    setFormData({
      ...formData,
      emails: formData.emails.map((e) => e.replace(oldEmail, email)),
    });
  };

  const handleDeleteEmail = (email: string) => {
    setFormData({
      ...formData,
      emails: formData.emails.filter((e) => e !== email),
    });
    setShowInputEmail(false);
  };

  const setAlertThreshold = async () => {
    setLoading(true);
    await changeAlertThreshold({
      amount: MonetaryAmount.fromUnitary(formData.amount),
      emails: formData.emails,
    });
    setLoading(false);
    hideModal();
  };

  return (
    <Modal>
      <Grid className={container}>
        <Grid container xs={12} item>
          <Typography className={titleStyles}>
            {translate("my-account-section.generate-notification")}
          </Typography>
        </Grid>
        <Typography className={description}>
          {translate(
            "my-account-section.select-amount-and-emails-that-will-receive-notification-message"
          )}
        </Typography>
        <Typography className={section}>
          {translate("my-account-section.when-balance-less-than")}
        </Typography>
        <Typography className={label}>
          {translate("my-account-section.balance-less-than")}
        </Typography>
        <Grid className={inputContainer}>
          <Input
            className={`${label} ${inputStyle}`}
            type="number"
            placeholder="5000 €"
            autoFocus
            value={formData.amount}
            onChange={(e) =>
              setFormData({
                ...formData,
                amount: e.target.value,
              })
            }
          />
        </Grid>
        <Typography className={section}>
          {translate("my-account-section.i-want-to-send-a-notification-to")}
        </Typography>
        {formData.emails.map((email) => (
          <EmailSection
            key={email}
            email={email}
            emailList={formData.emails}
            handleSave={handleSaveEmail}
            handleEdit={handleEditEmail}
            handleDelete={handleDeleteEmail}
          />
        ))}

        {showInputEmail && (
          <EmailSection
            email=""
            emailList={formData.emails}
            handleSave={handleSaveEmail}
            handleEdit={handleEditEmail}
            handleDelete={handleDeleteEmail}
          />
        )}

        <Grid className={linkContainer} onClick={showInput}>
          <FontAwesomeIcon icon="plus" className={`${label} ${linkStyle}`} />
          <Typography className={`${label} ${linkStyle}`}>
            {` ${translate("actions.add")} ${translate(
              "common-messages.email"
            )}`}
          </Typography>
        </Grid>
        <Grid container className={buttonContainer} justify="flex-end">
          <CobeeButton
            className={buttonSpacing}
            styleType="outlined"
            onClick={() => hideModal()}
          >
            {translate("actions.cancel")}
          </CobeeButton>

          <CobeeButton
            className={buttonSpacing}
            disabled={
              formData.amount === "" || formData.emails.length === 0 || loading
            }
            onClick={setAlertThreshold}
          >
            {loading
              ? translate("common-messages.sending")
              : translate("my-account-section.generate-warning")}
          </CobeeButton>
        </Grid>
      </Grid>
    </Modal>
  );
}

export default AlertThresholdModal;
